import { Trans } from "@lingui/react/macro";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PreviewIcon from "@mui/icons-material/Visibility";
import { Alert, alpha, Box, IconButton, Paper, Snackbar } from "@mui/material";
import { useState } from "react";

interface CodePreviewProps {
  code: string;
  onPreview?: () => void;
}

export default function CodePreview({ code, onPreview }: CodePreviewProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <>
      <Paper
        sx={{
          position: "relative",
          p: 2,
          bgcolor: "grey.900",
          color: "common.white",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 0.5,
          }}
        >
          {onPreview && (
            <IconButton
              size="small"
              onClick={onPreview}
              sx={{
                color: "grey.400",
                "&:hover": {
                  color: "common.white",
                  bgcolor: (theme) => alpha(theme.palette.common.white, 0.1),
                },
              }}
            >
              <PreviewIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: "grey.400",
              "&:hover": {
                color: "common.white",
                bgcolor: (theme) => alpha(theme.palette.common.white, 0.1),
              },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>

        <pre
          style={{
            margin: 0,
            fontFamily: "monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {code}
        </pre>
      </Paper>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setCopied(false)}>
          <Trans>Code copied to clipboard!</Trans>
        </Alert>
      </Snackbar>
    </>
  );
}
